import api from './api'

export const DASHBOARD_PERIODS = ['today', '7d', '30d'] as const
export type DashboardPeriod = (typeof DASHBOARD_PERIODS)[number]

export const DASHBOARD_PERIOD_LABELS: Record<DashboardPeriod, string> = {
  today: 'Hoy',
  '7d': '7 días',
  '30d': '30 días',
}

/** Días que cubre el período elegido (para la tendencia). */
export function dashboardPeriodDays(period: DashboardPeriod): number {
  return period === '30d' ? 30 : 7
}

export interface DashboardStats {
  /** Período de las métricas que lo admiten (`today` por defecto). */
  period?: DashboardPeriod
  overview: {
    messagesToday: number
    messagesThisWeek: number
    messagesThisMonth: number
    /** Entrantes + salientes del período elegido. */
    messagesInPeriod?: number
    messagesIncoming: number
    messagesOutgoing: number
    conversationsOpen: number
    conversationsClosed: number
    /** Cierres ocurridos dentro del período elegido. */
    conversationsClosedInPeriod?: number
    conversationsBot: number
    conversationsHuman: number
    conversationsAssistantInPeriod?: number
    conversationsHumanInPeriod?: number
    conversationsUnread: number
    activeUsers: number
    /** Porcentaje de conversaciones con al menos una respuesta; null = sin conversaciones. */
    responseRate: number | null
    /** Mediana del período en horas; null = sin respuestas medibles. */
    avgResponseTimeHours: number | null
    /** Conversaciones del período que no pasaron al equipo / total del período. */
    attendedByAssistantRate?: number | null
    deltas?: {
      messages: number | null
      attendedByAssistant: number | null
      responseTimeHours: number | null
    }
    totalCarts?: number
    totalQuotedAmount?: number
    /** Conversaciones cerradas por el asistente sin pasar al equipo; null = sin conversaciones. */
    autonomousResolutionRate?: number | null
    totalAppointments?: number
    pendingAppointments?: number
    confirmedAppointments?: number
    activeProducts?: number
  }
  instances: Array<{
    id: string
    name: string
    profileName?: string
    status: string
    createdAt: Date
    channelType?: string
    provider?: string
    metadata?: Record<string, any>
  }>
  teams: Array<{
    id: string
    name: string
    membersCount: number
    conversationsCount: number
  }>
  recentActivity: Array<{
    id: string
    contact: string
    message: string
    timestamp: Date
    direction: 'inbound' | 'outbound'
    instanceName: string
  }>
}

export interface PendingBotDoubt {
  id: string
  conversationId: string
  contactName: string
  waContact: string
  avatarUrl?: string | null
  channelType: string
  doubtText: string
  category:
    | 'unanswered_question'
    | 'human_requested'
    | 'frustration'
    | 'pricing'
    | 'stock'
    | 'appointment'
    | 'order_coordination'
    | 'general'
  categoryLabel: string
  reasonCode?: string | null
  status: 'pending' | 'queued' | 'escalated'
  isAssigned: boolean
  assignedUserName?: string | null
  assignedUserId?: string | null
  lastMessageAt: Date | string
  waitingTimeText: string
  orderDraftTotal?: number | null
  productName?: string | null
  unreadCount?: number
}

export interface MessageTrend {
  date: Date | string
  inbound: number
  outbound: number
}

/**
 * Get comprehensive dashboard statistics
 */
export const getDashboardStats = async (
  period: DashboardPeriod = 'today'
): Promise<DashboardStats> => {
  const response = await api.get('/dashboard/stats', { params: { period } })
  return response.data.data
}

/**
 * Get pending bot doubts requiring human attention
 */
export const getPendingBotDoubts = async (): Promise<PendingBotDoubt[]> => {
  const response = await api.get('/dashboard/pending-doubts')
  return response.data.data
}

/**
 * Get message trends over time
 */
export const getMessageTrends = async (days: number = 7): Promise<MessageTrend[]> => {
  const response = await api.get('/dashboard/trends', {
    params: { days },
  })
  return response.data.data
}

export interface ClosureReasonBreakdown {
  reason: string
  label: string
  category: 'won' | 'lost'
  count: number
  percentage: number
}

export interface ClosureTimelinePoint {
  date: string
  won: number
  lost: number
  winRate: number
}

export interface AgentClosureBreakdown {
  userId: string
  name: string
  totalClosed: number
  won: number
  lost: number
  winRate: number
}

export interface ClosedConversationExportItem {
  id: string
  contactName: string
  waContact: string
  closeReason: string
  closeReasonLabel: string
  category: 'won' | 'lost'
  closeNotes: string | null
  closedAt: string
  agentName: string
  orderTotal: number
}

export interface ClosureStatsResponse {
  summary: {
    totalClosed: number
    totalWon: number
    totalLost: number
    winRate: number
    totalRevenue: number
    avgTimeToCloseHours: number
  }
  byReason: ClosureReasonBreakdown[]
  timeline: ClosureTimelinePoint[]
  byAgent: AgentClosureBreakdown[]
  items: ClosedConversationExportItem[]
}

export interface ClosureStatsParams {
  days?: number
  startDate?: string
  endDate?: string
  userId?: string
}

/**
 * Obtener analítica y estadísticas de cierres de conversación
 */
export const getClosureStats = async (params: ClosureStatsParams = {}): Promise<ClosureStatsResponse> => {
  const response = await api.get('/dashboard/closure-stats', { params })
  return response.data.data
}


export interface WarrantyCoverage {
  /** El vertical vende productos con garantía variable. */
  applies: boolean
  totalProducts: number
  productsWithoutWarranty: number
  /** El tenant definió una política general de garantía como respaldo. */
  hasGeneralPolicy: boolean
}

/**
 * Cobertura de garantías del catálogo (productos sin garantía configurada).
 */
export const getWarrantyCoverage = async (): Promise<WarrantyCoverage> => {
  const response = await api.get('/dashboard/warranty-coverage')
  return response.data.data
}

export type UnmetDemandStatus = 'open' | 'dismissed' | 'stocked'

export interface UnmetDemandItem {
  id: string
  label: string
  /** Conversaciones distintas que lo pidieron. */
  requestCount: number
  status: UnmetDemandStatus
  lastCustomerMessage: string | null
  firstRequestedAt: string
  lastRequestedAt: string
  recentRequests: Array<{ conversationId: string; customerMessage: string | null; createdAt: string }>
}

/** Productos que pidieron los clientes y el inventario no tiene. */
export const getUnmetDemand = async (): Promise<UnmetDemandItem[]> => {
  const response = await api.get('/dashboard/unmet-demand', { params: { status: 'open', limit: 10 } })
  const data = response.data?.data
  return Array.isArray(data) ? (data as UnmetDemandItem[]) : []
}

export const updateUnmetDemandStatus = async (id: string, status: UnmetDemandStatus): Promise<void> => {
  await api.patch(`/dashboard/unmet-demand/${encodeURIComponent(id)}`, { status })
}
